import React, { useState, useEffect } from 'react';
import { TrendingUp, RefreshCw, Calendar } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для прогнозирования и отображения тенденций валютного курса
 */
const ForecastSidebar = ({ 
  formData,
  historicalRates,
  currencyRates,
  supportedCurrencies,
  exchangeRateScenarios
}) => {
  const [currentRate, setCurrentRate] = useState(0);
  const [historicalRate, setHistoricalRate] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState('1m'); // 1m, 3m, 6m, 1y
  const [yearlyChange, setYearlyChange] = useState(0);
  const [currentTrend, setCurrentTrend] = useState('stable'); // up, down, stable
  const [forecastRate, setForecastRate] = useState(0);
  const [scenarios, setScenarios] = useState([]);

  useEffect(() => {
    if (formData.currency && currencyRates[formData.currency]) {
      // Текущий курс
      const current = currencyRates[formData.currency];
      setCurrentRate(current);
      
      // Исторические данные
      if (historicalRates && historicalRates.length > 0) {
        let historicalIndex = 0;
        
        switch (selectedPeriod) {
          case '1m':
            historicalIndex = Math.min(30, historicalRates.length - 1);
            break;
          case '3m':
            historicalIndex = Math.min(90, historicalRates.length - 1);
            break;
          case '6m':
            historicalIndex = Math.min(180, historicalRates.length - 1);
            break;
          case '1y':
          default:
            historicalIndex = Math.min(365, historicalRates.length - 1);
            break;
        }
        
        const historical = historicalRates[historicalIndex][formData.currency] || 0;
        setHistoricalRate(historical);
        
        // Расчет годового изменения
        if (historical > 0) {
          const change = ((current - historical) / historical) * 100;
          setYearlyChange(change);
          
          // Определение тренда
          if (change > 2) {
            setCurrentTrend('up');
          } else if (change < -2) {
            setCurrentTrend('down');
          } else {
            setCurrentTrend('stable');
          }
        }
      }
      
      // Прогнозы по выбранному сценарию
      if (formData.exchangeRateScenario && exchangeRateScenarios) {
        const scenarioData = exchangeRateScenarios.find(s => s.id === formData.exchangeRateScenario);
        
        // Расчет прогнозов для всех сценариев
        const allScenarios = exchangeRateScenarios.map(scenario => {
          let annualChange = 0;
          
          switch (scenario.id) {
            case 'optimistic':
              annualChange = -10; // Укрепление рубля на 10%
              break;
            case 'moderate':
              annualChange = 5; // Ослабление рубля на 5%
              break;
            case 'pessimistic':
              annualChange = 15; // Ослабление рубля на 15%
              break;
            case 'crisis':
              annualChange = 30; // Ослабление рубля на 30%
              break;
            default:
              annualChange = 5;
          }
          
          const forecastedRate = current * (1 + annualChange / 100);
          
          return {
            id: scenario.id,
            name: scenario.name,
            description: scenario.description,
            annualChange,
            forecastedRate
          };
        });
        
        setScenarios(allScenarios);
        
        // Прогноз по выбранному сценарию
        const selectedScenario = allScenarios.find(s => s.id === formData.exchangeRateScenario);
        if (selectedScenario) {
          setForecastRate(selectedScenario.forecastedRate);
        }
      }
    }
  }, [formData.currency, formData.exchangeRateScenario, currencyRates, historicalRates, selectedPeriod, exchangeRateScenarios]);

  // Получение названия валюты
  const getCurrencyName = (code) => {
    const currency = supportedCurrencies.find(c => c.id === code);
    return currency ? currency.name : code;
  };

  // Получение цвета для тренда
  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-red-600 dark:text-red-400';
      case 'down':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Получение цвета для прогноза
  const getScenarioColor = (scenarioId) => {
    switch (scenarioId) {
      case 'optimistic':
        return 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300';
      case 'moderate':
        return 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300';
      case 'pessimistic':
        return 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300';
      case 'crisis':
        return 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-indigo-500" />
        Динамика курса валюты
      </h3>
      
      {formData.currency && (
        <div className="space-y-4">
          {/* Текущий курс и выбор периода */}
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Текущий курс</div>
              <div className="text-xl font-semibold">
                1 {formData.currency} = {formatNumber(currentRate, 2)} ₽
              </div>
            </div>
            <div className="flex gap-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {['1m', '3m', '6m', '1y'].map((period) => (
                <button
                  key={period}
                  className={`px-2 py-1 rounded-md ${
                    selectedPeriod === period 
                      ? 'bg-indigo-500 text-white' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  } transition-colors`}
                  onClick={() => setSelectedPeriod(period)}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          {/* Изменение курса */}
          <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div className="text-sm">Изменение за {
                selectedPeriod === '1m' ? '1 месяц' : 
                selectedPeriod === '3m' ? '3 месяца' : 
                selectedPeriod === '6m' ? '6 месяцев' : 
                '1 год'
              }</div>
              <div className={`font-medium ${getTrendColor(currentTrend)}`}>
                {yearlyChange > 0 ? '+' : ''}{formatNumber(yearlyChange, 2)}%
              </div>
            </div>
            <div className="mt-1 text-xs flex items-center text-gray-500 dark:text-gray-400">
              <Calendar className="w-3 h-3 mr-1" />
              {historicalRate > 0 && (
                <>
                  {selectedPeriod === '1m' ? '1 месяц' : 
                   selectedPeriod === '3m' ? '3 месяца' : 
                   selectedPeriod === '6m' ? '6 месяцев' : 
                   '1 год'} назад: 1 {formData.currency} = {formatNumber(historicalRate, 2)} ₽
                </>
              )}
            </div>
          </div>
          
          {/* Прогноз курса */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              <RefreshCw className="w-4 h-4 mr-1 text-indigo-500" />
              Прогноз курса на год вперед
            </h4>
            
            <div className="space-y-2">
              {scenarios.map((scenario) => (
                <div 
                  key={scenario.id}
                  className={`p-2 rounded-lg border flex justify-between items-center ${
                    formData.exchangeRateScenario === scenario.id 
                      ? getScenarioColor(scenario.id) + ' font-medium'
                      : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="text-sm">{scenario.name}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm">{formatNumber(scenario.forecastedRate, 2)} ₽</span>
                    <span className={`text-xs ${scenario.annualChange > 0 ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`}>
                      {scenario.annualChange > 0 ? '+' : ''}{scenario.annualChange}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              * Прогноз основан на исторических данных и выбранном сценарии. 
              Фактический курс может отличаться.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastSidebar;